/**
 * ResearchPanel — top-right card on ProfileEditor + CatalogueEditor
 * showing research metadata + the Regenerate action.
 *
 * Pulled out of ProfileEditor so the CatalogueEditor can reuse the same
 * layout in Phase 44.5 with `section="catalogue"`.
 *
 * Phase 44.4.
 */

import { useState } from "react";
import { useSetupStore } from "../../../stores/setupStore.js";

const CONFIDENCE_VARIANT = {
  high: "research-panel__badge--high",
  medium: "research-panel__badge--medium",
  low: "research-panel__badge--low",
};

export default function ResearchPanel({ section, metadata }) {
  const [confirming, setConfirming] = useState(false);
  const regenerate = useSetupStore((s) => s.regenerate);
  const regenerationJob = useSetupStore((s) => s.regenerationJob);
  const regenerationStatus = useSetupStore((s) => s.regenerationStatus);
  const regenerationError = useSetupStore((s) => s.regenerationError);

  const active =
    regenerationJob &&
    (!regenerationStatus ||
      !["SUCCEEDED", "FAILED", "TIMED_OUT", "ABORTED", "TIMED_OUT_POLL"].includes(
        regenerationStatus.status
      ));
  const matchesSection = regenerationJob?.mode === section;

  const confidence = metadata?.confidence;
  const sources = metadata?.sources?.length || 0;

  return (
    <aside className="research-panel">
      <h3 className="research-panel__heading">Research</h3>
      {confidence && (
        <span
          className={`research-panel__badge ${CONFIDENCE_VARIANT[confidence] || ""}`}
        >
          confidence: {confidence}
        </span>
      )}
      <dl className="research-panel__meta">
        {metadata?.generated_at && (
          <div>
            <dt>Generated</dt>
            <dd>{new Date(metadata.generated_at).toLocaleString()}</dd>
          </div>
        )}
        <div>
          <dt>Sources</dt>
          <dd>{sources}</dd>
        </div>
        {metadata?.last_edited_at && (
          <div>
            <dt>Last edited</dt>
            <dd>{new Date(metadata.last_edited_at).toLocaleString()}</dd>
          </div>
        )}
      </dl>

      {regenerationError && (
        <div className="research-panel__error" role="alert">
          {regenerationError}
        </div>
      )}

      {active && matchesSection ? (
        <div className="research-panel__status">
          Regenerating {section}…
          {regenerationStatus?.status && (
            <span className="research-panel__status-code">
              {" "}({regenerationStatus.status})
            </span>
          )}
        </div>
      ) : (
        <>
          {confirming ? (
            <div className="research-panel__confirm">
              <p className="research-panel__confirm-body">
                Regenerate will overwrite any edits you&apos;ve made to this
                section.
              </p>
              <div className="research-panel__confirm-actions">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setConfirming(false);
                    regenerate(section);
                  }}
                >
                  Regenerate
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-secondary btn-sm research-panel__regen"
              onClick={() => setConfirming(true)}
            >
              Regenerate from research
            </button>
          )}
        </>
      )}
    </aside>
  );
}
